import { useEffect, useState } from "react";
import { useParams, Link, useNavigate, useLocation } from "react-router-dom";
import { CheckCircle2, Package, ArrowRight, Printer } from "lucide-react";
import api from "@/lib/api";
import { usePageTitle } from "@/hooks/usePageTitle";

// Post-checkout "thank you" page. Two paths in:
// 1. Just placed an order: Checkout navigates here with the order object
//    already in useLocation().state so we render instantly, no fetch.
// 2. Bookmarked / shared: we hit GET /orders/:id and render on load.
//    Backend already RBAC-guards this (owner or order.read permission);
//    a 404 bounces the visitor to /dashboard.
export default function OrderConfirmation() {
  const { orderId } = useParams();
  const { state } = useLocation();
  const nav = useNavigate();
  const [order, setOrder] = useState(state?.order || null);
  const [error, setError] = useState(false);
  usePageTitle("Order confirmed");

  useEffect(() => {
    if (order) return;
    api.get(`/orders/${orderId}`)
      .then(({ data }) => setOrder(data))
      .catch(() => setError(true));
  }, [orderId, order]);

  if (error) {
    return (
      <div className="py-32 text-center">
        <div className="text-[10px] uppercase tracking-widest text-white/40 font-mono">Order not found</div>
        <h1 className="font-display text-3xl mt-4 tracking-tight">We could not load that order.</h1>
        <p className="mt-3 text-sm text-white/60">It may not exist, or you may not have access.</p>
        <button onClick={() => nav("/dashboard")}
                className="mt-8 inline-flex items-center gap-2 px-5 py-2.5 rounded-full gradient-bg font-medium text-sm">
          Back to your account
        </button>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="py-32 text-center">
        <div className="inline-block w-8 h-8 border-2 border-white/20 border-t-accent rounded-full animate-spin" />
      </div>
    );
  }

  const shortId = order.id.slice(0, 8).toUpperCase();
  const placed = order.created_at ? new Date(order.created_at).toLocaleString("en-IN", {
    dateStyle: "medium", timeStyle: "short",
  }) : null;

  return (
    <div className="py-16 lg:py-20">
      <div className="mx-auto max-w-3xl px-6 lg:px-10">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-500/15 border border-emerald-500/30">
            <CheckCircle2 className="w-7 h-7 text-emerald-300" />
          </div>
          <h1 className="font-display text-3xl lg:text-4xl tracking-tighter mt-6">
            Thanks, your order is in.
          </h1>
          <p className="mt-3 text-sm text-white/60">
            We have emailed you a receipt. Reference this order id if you need support: <span className="font-mono text-white">{shortId}</span>.
          </p>
        </div>

        <div className="mt-10 glass rounded-2xl p-6 print:bg-white print:text-black">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-white/40 font-mono">Order</div>
              <div className="font-display text-xl mt-1">{shortId}</div>
              {placed && <div className="text-xs text-white/50 mt-1">Placed {placed}</div>}
            </div>
            <button onClick={() => window.print()}
                    className="inline-flex items-center gap-2 text-xs px-4 py-2 rounded-full border border-white/15 hover:bg-white/5 print:hidden">
              <Printer className="w-3.5 h-3.5" /> Print receipt
            </button>
          </div>

          <div className="mt-6 divide-y divide-white/5">
            {(order.items || []).map((i, idx) => (
              <div key={idx} className="py-3 flex items-center justify-between text-sm">
                <div className="flex items-center gap-3 min-w-0">
                  <Package className="w-4 h-4 text-white/40 shrink-0" />
                  <div className="truncate">
                    <div className="truncate">{i.name}</div>
                    <div className="text-xs text-white/50">Qty {i.qty} · ₹{Number(i.price || 0).toLocaleString("en-IN")} each</div>
                  </div>
                </div>
                <div className="font-display text-accent shrink-0 ml-4">
                  ₹{(Number(i.price || 0) * Number(i.qty || 0)).toLocaleString("en-IN")}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 text-sm space-y-1.5">
            <div className="flex justify-between"><span className="text-white/60">Subtotal</span><span>₹{Number(order.subtotal || 0).toLocaleString("en-IN")}</span></div>
            <div className="flex justify-between"><span className="text-white/60">GST 18%</span><span>₹{Number(order.tax || 0).toLocaleString("en-IN")}</span></div>
            <div className="flex justify-between font-display text-lg mt-2 pt-2 border-t border-white/10">
              <span>Total</span>
              <span className="text-accent">₹{Number(order.total || 0).toLocaleString("en-IN")}</span>
            </div>
          </div>

          {order.affiliate_code && (
            <div className="mt-4 text-xs text-white/50 font-mono uppercase tracking-widest">
              Affiliate: {order.affiliate_code}
            </div>
          )}

          {order.shipping_address && (
            <div className="mt-6 text-sm text-white/70 leading-relaxed">
              <div className="text-[10px] uppercase tracking-widest text-white/40 font-mono mb-1">Ship to</div>
              <div>{order.shipping_address.name}</div>
              <div>{order.shipping_address.line1}</div>
              <div>{order.shipping_address.city}{order.shipping_address.state ? `, ${order.shipping_address.state}` : ""} {order.shipping_address.pincode}</div>
              {order.shipping_address.phone && <div className="text-white/50 mt-1">{order.shipping_address.phone}</div>}
            </div>
          )}
        </div>

        <div className="mt-8 flex flex-wrap gap-3 justify-center print:hidden">
          <Link to="/dashboard" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full gradient-bg text-sm font-medium">
            View all orders <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/marketplace" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/15 hover:bg-white/5 text-sm">
            Keep shopping
          </Link>
        </div>
      </div>
    </div>
  );
}
